import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useSite } from '../../lib/site';
import { Alert, Badge, Button, Card, ConfirmButton, EmptyRow, PageHeader, Spinner, TableWrap, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { saveBlob } from '../../components/ExportButton';
import { RollCallList } from '../../components/phase3';
import { durationText, evacKey, useEvacuationView, useReport } from './api';

function Tile({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-2xl font-bold tabular-nums text-slate-900">{value}</p>
      <p className="text-xs text-slate-600">{label}</p>
    </div>
  );
}

/** One evacuation's report: summary, safe-by-point, missing people, timeline, roll call, PDF and delete. */
export function ReportDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { sites } = useSite();
  const canDelete = useCan('evacuation.reports_delete');
  const view = useEvacuationView(id);
  const report = useReport(id);
  const [pdfError, setPdfError] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.delete(`/evacuation/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: evacKey });
      void navigate('/reports/evacuations');
    },
  });
  const pdf = useMutation({
    mutationFn: async () => {
      const { blob, filename } = await api.download(`/evacuation/report/${id}/pdf`);
      saveBlob(blob, filename ?? 'evacuation-report.pdf');
    },
    onMutate: () => setPdfError(false),
    onError: () => setPdfError(true),
  });

  const back = (
    <Link to="/reports/evacuations" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-[var(--brand-primary)] underline">
      <ArrowLeft aria-hidden="true" className="h-4 w-4" />
      {t('evac.detail.back')}
    </Link>
  );

  if (view.isError) return <div>{back}<ErrorPanel error={view.error} onRetry={() => void view.refetch()} /></div>;
  if (!view.data) return <div>{back}<Spinner /></div>;
  const ev = view.data.evacuation;
  const site = sites.find((s) => s.id === ev.siteId);
  const zone = site?.timeZone;
  const r = report.data;

  return (
    <div>
      {back}
      <PageHeader
        title={t('evac.detail.title', { kind: t(`evac.kind.${ev.kind}`), site: site?.name ?? '' })}
        description={`${formatDateTime(ev.startedAt, zone)}${ev.endedAt ? ` - ${formatDateTime(ev.endedAt, zone)}` : ''}`}
        actions={
          ev.status === 'ended' ? (
            <Button onClick={() => pdf.mutate()} disabled={pdf.isPending || !ev.reportReady}>
              <Download aria-hidden="true" className="h-4 w-4" />
              {t('evac.detail.pdf')}
            </Button>
          ) : null
        }
      />
      <div className="space-y-6">
        {pdfError ? <Alert>{t('evac.detail.pdfFailed')}</Alert> : null}
        {ev.updatedAfterEnd ? <Alert kind="info">{t('evac.detail.updatedNote')}</Alert> : null}
        {ev.status === 'active' ? (
          <Alert kind="info">
            {t('evac.detail.stillActive')}{' '}
            <Link className="font-medium underline" to="/evacuation">
              {t('evac.detail.openLive')}
            </Link>
          </Alert>
        ) : null}

        <section aria-label={t('evac.detail.summary')} className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Tile label={t('evac.detail.total')} value={ev.counts.total} />
          <Tile label={t('evac.detail.safe')} value={ev.counts.safe} />
          <Tile label={t('evac.detail.missing')} value={ev.counts.missing} />
          <Tile label={t('evac.detail.offSite')} value={ev.counts.offSite} />
          <Tile label={t('evac.detail.unaccounted')} value={ev.counts.unaccounted} />
          {r ? <Tile label={t('evac.detail.duration')} value={durationText(r.durationSeconds, t) || '-'} /> : null}
        </section>
        {r ? (
          <p className="text-sm text-slate-700">
            {r.allAccountedSeconds !== null ? `${t('evac.detail.allAccounted')}: ${durationText(r.allAccountedSeconds, t)}` : t('evac.detail.neverAccounted')}
          </p>
        ) : null}
        {report.isError && ev.status === 'ended' ? <ErrorPanel error={report.error} onRetry={() => void report.refetch()} /> : null}

        {r ? (
          <>
            <Card title={t('evac.detail.byPoint')}>
              {r.byPoint.length === 0 ? (
                <p className="text-sm text-slate-600">{t('evac.detail.byPointNone')}</p>
              ) : (
                <ul className="divide-y divide-slate-200 text-sm">
                  {r.byPoint.map((p) => (
                    <li key={p.pointId ?? 'none'} className="flex justify-between py-2">
                      <span>{p.pointId ? p.pointName : t('evac.detail.noPoint')}</span>
                      <span className="tabular-nums font-semibold">{p.safe}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title={t('evac.detail.missingList')}>
              {r.missing.length === 0 ? (
                <p className="text-sm text-slate-600">{t('evac.detail.missingNone')}</p>
              ) : (
                <TableWrap caption={t('evac.detail.missingList')}>
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr>
                      <Th>{t('evac.detail.col.person')}</Th>
                      <Th>{t('evac.detail.col.group')}</Th>
                      <Th>{t('evac.detail.col.host')}</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {r.missing.map((m, i) => (
                      <tr key={i}>
                        <td className="px-4 py-2 font-medium">{m.name}</td>
                        <td className="px-4 py-2">{m.groupName}</td>
                        <td className="px-4 py-2">{m.hostName ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </TableWrap>
              )}
            </Card>

            <section aria-labelledby="evac-timeline" className="space-y-2">
              <h2 id="evac-timeline" className="text-base font-semibold text-slate-900">
                {t('evac.detail.timeline')}
              </h2>
              <TableWrap caption={t('evac.detail.timeline')}>
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <Th>{t('evac.detail.col.time')}</Th>
                    <Th>{t('evac.detail.col.person')}</Th>
                    <Th>{t('evac.detail.col.status')}</Th>
                    <Th>{t('evac.detail.col.by')}</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {r.timeline.length === 0 ? <EmptyRow cols={4} /> : null}
                  {r.timeline.map((x, i) => (
                    <tr key={i} className={x.applied ? '' : 'text-slate-500'}>
                      <td className="px-4 py-2 tabular-nums">{formatDateTime(x.at, zone)}</td>
                      <td className="px-4 py-2 font-medium">{x.name}</td>
                      <td className="px-4 py-2">
                        {t(`evac.status.${x.status}`)} {!x.applied ? <Badge>{t('evac.detail.notApplied')}</Badge> : null}
                      </td>
                      <td className="px-4 py-2">
                        {x.byLabel} ({t(`evac.mark.${x.source}`)})
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableWrap>
            </section>
          </>
        ) : null}

        <section aria-labelledby="evac-roll" className="space-y-2">
          <h2 id="evac-roll" className="text-base font-semibold text-slate-900">
            {t('evac.detail.rollCall')}
          </h2>
          <RollCallList people={view.data.people} points={view.data.points} readOnly />
        </section>

        {canDelete && ev.status === 'ended' ? (
          <Card title={t('evac.detail.delete')} description={t('evac.detail.deleteHint')}>
            <ErrorLine error={remove.error} />
            <ConfirmButton label={t('evac.detail.delete')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
          </Card>
        ) : null}
      </div>
    </div>
  );
}
